import {
    type CSSProperties,
    useState,
    useRef
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type DateTimePickerDateRange,
    type IYearSelectorRef,
    NCoreUIKitLocalize,
    formatDateTime,
    YearSelector,
    Button
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "YearSelector";

const USAGE_CODE = `const [date, setDate] = useState<Date>();

<YearSelector
    selectMultipleObject={() => undefined}
    selectObject={setDate}
    variant="single"
    date={date}
/>`;

const RANGE_CODE = `const [dateRange, setDateRange] = useState<DateTimePickerDateRange>();

<YearSelector
    selectMultipleObject={setDateRange}
    selectObject={() => undefined}
    multipleSelectDayLimit={3}
    dateRange={dateRange}
    variant="range"
/>`;

const LIMITS_CODE = `<YearSelector
    selectMultipleObject={() => undefined}
    maxDate={new Date(2034, 11, 31)}
    minDate={new Date(2024, 0, 1)}
    selectObject={setDate}
    variant="single"
    date={date}
/>`;

const REF_CODE = `const yearRef = useRef<IYearSelectorRef>(null);

<YearSelector
    selectMultipleObject={() => undefined}
    selectObject={setDate}
    ref={yearRef}
    variant="single"
    date={date}
/>

yearRef.current?.changeCurrentView(new Date());`;

const CONTROLS_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 12
};

const YEAR_FORMAT: Intl.DateTimeFormatOptions = {
    year: "numeric"
};

const TODAY = new Date();

const addYears = (date: Date, count: number) => new Date(date.getFullYear() + count, 0, 1);

const RANGE_MAX_YEARS = 3;

const LIMIT_MIN_DATE = addYears(TODAY, -2);

const LIMIT_MAX_DATE = new Date(TODAY.getFullYear() + 8, 11, 31);

const JUMP_YEARS = 30;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("yearSelectorPage.rangeTitle"),
        id: "range"
    },
    {
        title: t("yearSelectorPage.limitsTitle"),
        id: "limits"
    },
    {
        title: t("yearSelectorPage.windowTitle"),
        id: "window"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const YearSelectorPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const yearRef = useRef<IYearSelectorRef>(null);

    const [
        date,
        setDate
    ] = useState<Date | undefined>(undefined);

    const [
        dateRange,
        setDateRange
    ] = useState<DateTimePickerDateRange | undefined>(undefined);

    const [
        limitDate,
        setLimitDate
    ] = useState<Date | undefined>(undefined);

    const [
        viewedDate,
        setViewedDate
    ] = useState<Date | undefined>(undefined);

    const formatYear = (value: Date) => formatDateTime({
        locale: activeLocale,
        options: YEAR_FORMAT,
        date: value
    });

    const getDateCaption = () => {
        if(!date) {
            return t("yearSelectorPage.overviewCaption");
        }

        return t("yearSelectorPage.selectedCaption", [
            formatYear(date)
        ]);
    };

    const getRangeCaption = () => {
        if(!dateRange?.start) {
            return t("yearSelectorPage.rangeEmptyCaption", [
                RANGE_MAX_YEARS
            ]);
        }

        if(!dateRange.end) {
            return t("yearSelectorPage.rangeStartCaption", [
                formatYear(dateRange.start)
            ]);
        }

        return t("yearSelectorPage.rangeCaption", [
            formatYear(dateRange.start),
            formatYear(dateRange.end)
        ]);
    };

    const getLimitCaption = () => {
        if(!limitDate) {
            return t("yearSelectorPage.limitsCaption", [
                formatYear(LIMIT_MIN_DATE),
                formatYear(LIMIT_MAX_DATE)
            ]);
        }

        return t("yearSelectorPage.selectedCaption", [
            formatYear(limitDate)
        ]);
    };

    const handleThisYear = () => {
        yearRef.current?.changeCurrentView(new Date());
    };

    const handleJumpAhead = () => {
        yearRef.current?.changeCurrentView(addYears(TODAY, JUMP_YEARS));
    };

    return <Article>
        <ComponentHeader
            lead={t("yearSelectorPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("yearSelectorPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("yearSelectorPage.overviewValueText")}
            </Paragraph>
            <Preview
                caption={getDateCaption()}
            >
                <YearSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setDate}
                    variant="single"
                    date={date}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("yearSelectorPage.rangeTitle")}
            id="range"
        >
            <Paragraph>
                {t("yearSelectorPage.rangeText")}
            </Paragraph>
            <Preview
                caption={getRangeCaption()}
            >
                <YearSelector
                    multipleSelectDayLimit={RANGE_MAX_YEARS}
                    selectMultipleObject={setDateRange}
                    selectObject={() => undefined}
                    dateRange={dateRange}
                    variant="range"
                />
            </Preview>
            <CodeBlock
                code={RANGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("yearSelectorPage.limitsTitle")}
            id="limits"
        >
            <Paragraph>
                {t("yearSelectorPage.limitsText")}
            </Paragraph>
            <Preview
                caption={getLimitCaption()}
            >
                <YearSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setLimitDate}
                    maxDate={LIMIT_MAX_DATE}
                    minDate={LIMIT_MIN_DATE}
                    date={limitDate}
                    variant="single"
                />
            </Preview>
            <CodeBlock
                code={LIMITS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("yearSelectorPage.windowTitle")}
            id="window"
        >
            <Paragraph>
                {t("yearSelectorPage.windowText")}
            </Paragraph>
            <Preview>
                <YearSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setViewedDate}
                    date={viewedDate}
                    variant="single"
                    ref={yearRef}
                />
                <div
                    style={CONTROLS_STYLE}
                >
                    <Button
                        title={t("yearSelectorPage.goThisYear")}
                        onPress={handleThisYear}
                        variant="outline"
                        type="neutral"
                    />
                    <Button
                        title={t("yearSelectorPage.goAhead", [
                            JUMP_YEARS
                        ])}
                        onPress={handleJumpAhead}
                        variant="outline"
                        type="neutral"
                    />
                </div>
            </Preview>
            <CodeBlock
                code={REF_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("yearSelectorPage.a11yStructure"),
                    t("yearSelectorPage.a11yYear"),
                    t("yearSelectorPage.a11yState"),
                    t("yearSelectorPage.a11yPeriod"),
                    t("yearSelectorPage.a11yKeyboard"),
                    t("yearSelectorPage.a11yLimit"),
                    t("yearSelectorPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("yearSelectorPage.propVariant"),
                        type: "\"single\" | \"range\" | \"rrule\"",
                        name: "variant"
                    },
                    {
                        description: t("yearSelectorPage.propSelectObject"),
                        type: "(date?: Date) => void",
                        name: "selectObject"
                    },
                    {
                        description: t("yearSelectorPage.propSelectMultipleObject"),
                        type: "(dateRange?: DateTimePickerDateRange) => void",
                        name: "selectMultipleObject"
                    },
                    {
                        description: t("yearSelectorPage.propDate"),
                        name: "date",
                        type: "Date"
                    },
                    {
                        description: t("yearSelectorPage.propDateRange"),
                        type: "DateTimePickerDateRange",
                        name: "dateRange"
                    },
                    {
                        description: t("yearSelectorPage.propViewDate"),
                        name: "viewDate",
                        type: "Date"
                    },
                    {
                        description: t("yearSelectorPage.propMinDate"),
                        name: "minDate",
                        type: "Date"
                    },
                    {
                        description: t("yearSelectorPage.propMaxDate"),
                        name: "maxDate",
                        type: "Date"
                    },
                    {
                        description: t("yearSelectorPage.propMultipleSelectDayLimit"),
                        name: "multipleSelectDayLimit",
                        type: "number"
                    },
                    {
                        description: t("yearSelectorPage.propMultipleSelectMinimumRequiredDayCount"),
                        name: "multipleSelectMinimumRequiredDayCount",
                        type: "number"
                    },
                    {
                        description: t("yearSelectorPage.propIsShowTodayIndicator"),
                        name: "isShowTodayIndicator",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("yearSelectorPage.propIsShowCard"),
                        defaultValue: "true",
                        name: "isShowCard",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("yearSelectorPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("yearSelectorPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph>
                {t("yearSelectorPage.propsRefIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("yearSelectorPage.refChangeCurrentView"),
                        type: "(date: Date) => void",
                        name: "changeCurrentView"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("yearSelectorPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default YearSelectorPage;
